import { useContext } from "react";
import { SidebarContext } from "src/components/Context/SidebarContext";
import Scrollbar from "src/components/Scrollbar";
import CloseTwoToneIcon from "@mui/icons-material/CloseTwoTone";

import {
	Box,
	Button,
	Divider,
	Drawer,
	IconButton,
	styled,
	Typography,
	useTheme,
} from "@mui/material";

import Logo from "src/components/LogoSign";
import SidebarMenu from "./SidebarMenu/SidebarMenu";

const SidebarWrapper = styled(Box)(
	({ theme }) => `
        width: ${theme.sidebar.width};
        min-width: ${theme.sidebar.width};
        color: rgba(226, 232, 240, 0.8);
        position: relative;
        z-index: 7;
        height: 100%;
        padding-bottom: 60px;
`
);

function Sidebar() {
	const { sidebarToggle, toggleSidebar } = useContext(SidebarContext);
	const closeSidebar = () => toggleSidebar();
	const theme = useTheme();

	return (
		<>
			{/* Desktop Permanent Sidebar */}
			<SidebarWrapper
				sx={{
					display: {
						xs: "none",
						lg: "inline-block",
					},
					position: "fixed",
					left: 0,
					top: 0,
					background: "#0F172A !important", // Modern Slate 900
					borderRight: "1px solid rgba(255, 255, 255, 0.08)",
					boxShadow: "4px 0 24px rgba(0, 0, 0, 0.25)",
				}}
			>
				<Scrollbar>
					{/* Logo Header */}
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							justifyContent: "center",
							width: "100%",
							pt: 1.75,
							pb: 1,
							px: 1.5,
						}}
					>
						<Logo />
					</Box>

					<Divider
						sx={{
							mx: 2,
							mb: 0.5,
							borderColor: "rgba(255, 255, 255, 0.08)",
						}}
					/>

					{/* Navigation Menu */}
					<SidebarMenu />
				</Scrollbar>

				{/* Bottom Footer Bar */}
				<Box
					sx={{
						position: "absolute",
						bottom: 0,
						left: 0,
						right: 0,
						p: 1.5,
						borderTop: "1px solid rgba(255, 255, 255, 0.08)",
						background: "#0B1320",
					}}
				>
					<Button
						href="https://bttf.net.bd/"
						target="_blank"
						rel="noopener noreferrer"
						size="small"
						fullWidth
						sx={{
							backgroundColor: "rgba(12, 141, 45, 0.15) !important",
							color: "#4ADE80",
							border: "1px solid rgba(12, 141, 45, 0.3)",
							fontWeight: 600,
							fontSize: "0.75rem",
							letterSpacing: "0.3px",
							textTransform: "none",
							borderRadius: "7px",
							py: 0.6,
							transition: "all 0.2s ease",
							"&:hover": {
								backgroundColor: "#0C8D2D !important",
								color: "#FFFFFF",
								borderColor: "#0C8D2D",
								boxShadow: "0 2px 8px rgba(12, 141, 45, 0.35)",
							},
						}}
					>
						&copy; BTTF {new Date().getFullYear()} • Portal
					</Button>
				</Box>
			</SidebarWrapper>

			{/* Mobile / Tablet Drawer */}
			<Drawer
				sx={{
					zIndex: 1300,
					"& .MuiDrawer-paper": {
						background: "#0F172A !important",
						width: { xs: 285, sm: 300 },
						boxShadow: "0 8px 32px rgba(0, 0, 0, 0.6)",
						borderRight: "1px solid rgba(255, 255, 255, 0.08)",
					},
				}}
				anchor={theme.direction === "rtl" ? "right" : "left"}
				open={sidebarToggle}
				onClose={closeSidebar}
				variant="temporary"
				elevation={9}
			>
				<Box
					sx={{
						width: "100%",
						height: "100%",
						color: "rgba(226, 232, 240, 0.8)",
						display: "flex",
						flexDirection: "column",
					}}
				>
					{/* Mobile Drawer Top Header */}
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							px: 1.5,
							pt: 1.5,
							pb: 1,
						}}
					>
						<Box sx={{ maxWidth: 150, flex: 1 }}>
							<Logo />
						</Box>

						<IconButton
							onClick={closeSidebar}
							size="small"
							sx={{
								color: "rgba(255, 255, 255, 0.7)",
								bgcolor: "rgba(255, 255, 255, 0.06)",
								"&:hover": {
									bgcolor: "rgba(255, 255, 255, 0.15)",
									color: "#FFFFFF",
								},
								borderRadius: "6px",
								p: 0.6,
							}}
						>
							<CloseTwoToneIcon fontSize="small" />
						</IconButton>
					</Box>

					<Divider
						sx={{
							mx: 2,
							borderColor: "rgba(255, 255, 255, 0.08)",
						}}
					/>

					{/* Menu items */}
					<Box sx={{ flex: 1, overflow: "hidden" }}>
						<Scrollbar>
							<SidebarMenu />
						</Scrollbar>
					</Box>

					{/* Mobile Drawer Footer */}
					<Box
						sx={{
							p: 1.5,
							borderTop: "1px solid rgba(255, 255, 255, 0.08)",
							background: "#0B1320",
						}}
					>
						<Button
							href="https://bttf.net.bd/"
							target="_blank"
							rel="noopener noreferrer"
							size="small"
							fullWidth
							sx={{
								backgroundColor: "rgba(12, 141, 45, 0.15) !important",
								color: "#4ADE80",
								border: "1px solid rgba(12, 141, 45, 0.3)",
								fontWeight: 600,
								fontSize: "0.75rem",
								letterSpacing: "0.3px",
								textTransform: "none",
								borderRadius: "7px",
								py: 0.6,
								transition: "all 0.2s ease",
								"&:hover": {
									backgroundColor: "#0C8D2D !important",
									color: "#FFFFFF",
									borderColor: "#0C8D2D",
									boxShadow: "0 2px 8px rgba(12, 141, 45, 0.35)",
								},
							}}
						>
							&copy; BTTF {new Date().getFullYear()} • Portal
						</Button>
					</Box>
				</Box>
			</Drawer>
		</>
	);
}

export default Sidebar;
